import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

const SlideContext = createContext(null);

export const SlideProvider = ({ children }) => {
  const hideTimerRef = useRef(null);
  const [state, setState] = useState({ 
    isVisible: false, 
    imageUrl: null, 
    expiresAt: 0,
    lastShownDataset: null, // Track the last dataset that triggered a slide
    slideDisplayed: false   // Track if current slide has been displayed
  });

  const hide = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
    setState((s) => ({ 
      ...s, 
      isVisible: false, 
      imageUrl: null, 
      expiresAt: 0,
      slideDisplayed: false
    }));
  }, []);

  const show = useCallback((url, durationMs = 12000, datasetId = null) => {
    if (!url) return;
    const now = Date.now();
    const expiresAt = now + durationMs;

    // Check if this is the same dataset that was already shown
    const isSameDataset = datasetId && state.lastShownDataset === datasetId;
    
    // If it's the same dataset and we've already displayed it, don't show again
    if (isSameDataset && state.slideDisplayed) {
      console.log("Skipping slide display - same dataset already shown");
      return;
    }

    setState({ 
      isVisible: true, 
      imageUrl: url, 
      expiresAt,
      lastShownDataset: datasetId,
      slideDisplayed: true
    });

    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      hide();
    }, durationMs);
  }, [hide, state.lastShownDataset, state.slideDisplayed]);

  const value = useMemo(() => ({
    isVisible: state.isVisible,
    imageUrl: state.imageUrl,
    expiresAt: state.expiresAt,
    lastShownDataset: state.lastShownDataset,
    slideDisplayed: state.slideDisplayed,
    show,
    hide,
  }), [state.isVisible, state.imageUrl, state.expiresAt, state.lastShownDataset, state.slideDisplayed, show, hide]);

  return <SlideContext.Provider value={value}>{children}</SlideContext.Provider>;
};

export const useSlide = () => useContext(SlideContext);


